import {
  DayPartSections,
  FilterDocument,
  FilterOptionKeys,
  goalInboxID,
  GroupByMode,
  GroupSetting,
  ID,
  lifeAreaOtherID,
  noDeadlineSectionID,
  noGroupingId,
  overdueSectionID,
  TaskPriorityOptions,
  TypeSections,
  untaggedId,
} from '@griply/types';
import { addDays, endOfDay } from 'date-fns';

import { parseDateKey } from '../format-date-key';
import { getDayPartStartTime } from '../task-list-reorder/get-day-part-start-time';

export type NewTaskPrefill = {
  goalId?: ID;
  lifeAreaId?: ID;
  sectionId?: ID;
  priority?: TaskPriorityOptions;
  tagIds?: ID[];
  startDate?: Date;
  startTime?: number;
  deadline?: Date;
  deadlineDeadline?: Date;
  isHabit?: boolean;
};

export type NewTaskPrefillRouteParams = {
  goalId?: ID;
  lifeAreaId?: ID;
  sectionId?: ID;
  priority?: string;
  tagIds?: string;
  startDate?: string;
  startTime?: string;
  deadline?: string;
  deadlineDeadline?: string;
  isHabit?: string;
};

const IS_HABIT_PARAM_TRUE = 'true';

export type GetNewTaskPrefillOptions = {
  groupByMode: GroupByMode;
  groupSectionId: ID;
  groupSetting: GroupSetting;
  today: Date;
  defaultNewTaskDate?: Date;
  filterDocument?: FilterDocument;
};

const pickSingleInclude = <T extends string>(
  include: T[] | null | undefined,
): T | undefined => (include?.length === 1 ? include[0] : undefined);

const dedupe = <T>(values: T[]): T[] => Array.from(new Set(values));

export const getNewTaskPrefill = ({
  groupByMode,
  groupSectionId,
  groupSetting,
  today,
  defaultNewTaskDate,
  filterDocument,
}: GetNewTaskPrefillOptions): NewTaskPrefill => {
  const contextOverlay = {
    goalId: groupSetting.goalId,
    lifeAreaId: groupSetting.lifeAreaId,
    tagIds: groupSetting.tagId ? [groupSetting.tagId] : undefined,
  };

  if (groupByMode === GroupByMode.Goal) {
    return {
      goalId: groupSectionId === goalInboxID ? undefined : groupSectionId,
      lifeAreaId: contextOverlay.lifeAreaId,
      tagIds: contextOverlay.tagIds,
      deadline: defaultNewTaskDate,
    };
  }

  if (groupByMode === GroupByMode.LifeArea) {
    return {
      lifeAreaId:
        groupSectionId === lifeAreaOtherID ? undefined : groupSectionId,
      tagIds: contextOverlay.tagIds,
      deadline: defaultNewTaskDate,
    };
  }

  if (groupByMode === GroupByMode.Priority) {
    const priority =
      groupSectionId === TaskPriorityOptions.None
        ? undefined
        : (groupSectionId as TaskPriorityOptions);
    return {
      priority,
      goalId: contextOverlay.goalId,
      lifeAreaId: contextOverlay.lifeAreaId,
      tagIds: contextOverlay.tagIds,
      deadline: defaultNewTaskDate,
    };
  }

  if (groupByMode === GroupByMode.Tag) {
    const sectionTag =
      groupSectionId === untaggedId ? undefined : groupSectionId;
    const merged = [sectionTag, groupSetting.tagId].filter(
      (id): id is ID => !!id,
    );
    return {
      goalId: contextOverlay.goalId,
      lifeAreaId: contextOverlay.lifeAreaId,
      tagIds: merged.length ? dedupe(merged) : undefined,
      deadline: defaultNewTaskDate,
    };
  }

  if (groupByMode === GroupByMode.Type) {
    const isHabit = groupSectionId === TypeSections.Habits;
    return {
      goalId: contextOverlay.goalId,
      lifeAreaId: contextOverlay.lifeAreaId,
      tagIds: contextOverlay.tagIds,
      deadline: isHabit ? undefined : defaultNewTaskDate,
      isHabit: isHabit || undefined,
    };
  }

  if (groupByMode === GroupByMode.DayPart) {
    const startTime = getDayPartStartTime(groupSectionId as DayPartSections);
    return {
      lifeAreaId: contextOverlay.lifeAreaId,
      tagIds: contextOverlay.tagIds,
      startDate: today,
      startTime: startTime ?? undefined,
      deadline: defaultNewTaskDate,
    };
  }

  if (groupByMode === GroupByMode.Section) {
    return {
      sectionId: groupSectionId === noGroupingId ? undefined : groupSectionId,
      goalId: contextOverlay.goalId,
      lifeAreaId: contextOverlay.lifeAreaId,
      tagIds: contextOverlay.tagIds,
      deadline: defaultNewTaskDate,
    };
  }

  if (groupByMode === GroupByMode.Deadline) {
    const deadline = resolveDeadlineFromSection(groupSectionId, today);
    return {
      goalId: contextOverlay.goalId,
      lifeAreaId: contextOverlay.lifeAreaId,
      tagIds: contextOverlay.tagIds,
      deadline,
    };
  }

  if (groupByMode === GroupByMode.DeadlineDeadline) {
    const deadlineDeadline = resolveDeadlineFromSection(groupSectionId, today);
    return {
      goalId: contextOverlay.goalId,
      lifeAreaId: contextOverlay.lifeAreaId,
      tagIds: contextOverlay.tagIds,
      deadlineDeadline,
    };
  }

  if (groupByMode === GroupByMode.UpcomingDeadline) {
    return {
      deadline: parseDateKey(groupSectionId),
    };
  }

  // GroupByMode.None — fall back to single-include filter values when no
  // groupSetting context is supplied.
  const goalFilterId = pickSingleInclude<ID>(
    filterDocument?.filters?.[FilterOptionKeys.Goal]?.include,
  );
  const lifeAreaFilterId = pickSingleInclude<ID>(
    filterDocument?.filters?.[FilterOptionKeys.LifeArea]?.include,
  );
  const tagFilterIds =
    filterDocument?.filters?.[FilterOptionKeys.Tag]?.include?.length === 1
      ? filterDocument?.filters?.[FilterOptionKeys.Tag]?.include
      : undefined;

  return {
    goalId: groupSetting.goalId ?? goalFilterId,
    lifeAreaId: groupSetting.lifeAreaId ?? lifeAreaFilterId,
    tagIds: groupSetting.tagId
      ? [groupSetting.tagId]
      : tagFilterIds ?? undefined,
    deadline: defaultNewTaskDate,
  };
};

const resolveDeadlineFromSection = (
  sectionId: ID,
  today: Date,
): Date | undefined => {
  if (sectionId === noDeadlineSectionID) {
    return undefined;
  }
  if (sectionId === overdueSectionID) {
    return endOfDay(addDays(today, -1));
  }
  return parseDateKey(sectionId);
};

const parseDate = (raw: string | undefined): Date | undefined => {
  if (!raw) {
    return undefined;
  }
  const parsed = new Date(raw);
  if (Number.isNaN(parsed.getTime())) {
    return undefined;
  }
  return parsed;
};

const parseNumber = (raw: string | undefined): number | undefined => {
  if (raw === undefined) {
    return undefined;
  }
  const parsed = Number(raw);
  if (Number.isNaN(parsed)) {
    return undefined;
  }
  return parsed;
};

const parseTagIds = (raw: string | undefined): ID[] | undefined => {
  if (!raw) {
    return undefined;
  }
  const ids = raw
    .split(',')
    .map((id) => id.trim())
    .filter(Boolean) as ID[];
  if (!ids.length) {
    return undefined;
  }
  return ids;
};

const parsePriority = (
  raw: string | undefined,
): TaskPriorityOptions | undefined => {
  if (!raw) {
    return undefined;
  }
  const known = Object.values(TaskPriorityOptions) as string[];
  if (!known.includes(raw)) {
    return undefined;
  }
  return raw as TaskPriorityOptions;
};

export const parseNewTaskPrefill = (
  raw: NewTaskPrefillRouteParams,
): NewTaskPrefill => ({
  goalId: raw.goalId,
  lifeAreaId: raw.lifeAreaId,
  sectionId: raw.sectionId,
  priority: parsePriority(raw.priority),
  tagIds: parseTagIds(raw.tagIds),
  startDate: parseDate(raw.startDate),
  startTime: parseNumber(raw.startTime),
  deadline: parseDate(raw.deadline),
  deadlineDeadline: parseDate(raw.deadlineDeadline),
  isHabit: raw.isHabit === IS_HABIT_PARAM_TRUE ? true : undefined,
});

export const serializeNewTaskPrefill = (
  prefill: NewTaskPrefill,
): NewTaskPrefillRouteParams => {
  const params: NewTaskPrefillRouteParams = {};
  if (prefill.goalId) {
    params.goalId = prefill.goalId;
  }
  if (prefill.lifeAreaId) {
    params.lifeAreaId = prefill.lifeAreaId;
  }
  if (prefill.sectionId) {
    params.sectionId = prefill.sectionId;
  }
  if (prefill.priority) {
    params.priority = prefill.priority;
  }
  if (prefill.tagIds?.length) {
    params.tagIds = prefill.tagIds.join(',');
  }
  if (prefill.startDate) {
    params.startDate = prefill.startDate.toISOString();
  }
  if (typeof prefill.startTime === 'number') {
    params.startTime = String(prefill.startTime);
  }
  if (prefill.deadline) {
    params.deadline = prefill.deadline.toISOString();
  }
  if (prefill.deadlineDeadline) {
    params.deadlineDeadline = prefill.deadlineDeadline.toISOString();
  }
  if (prefill.isHabit) {
    params.isHabit = IS_HABIT_PARAM_TRUE;
  }
  return params;
};
